import {
  createUserWithEmailAndPassword,
  getAuth,
  ActionCodeSettings
} from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import staffService from './staffService';
import emailCustomizationService from './emailCustomizationService';
import userService from './userService';

/**
 * Staff Invitation Service - Handles operations related to staff invitations and account creation
 */
const staffInvitationService = {
  /**
   * Create a Firebase Auth account for a staff member and send an invitation email
   * @param {string} brandId - Brand ID
   * @param {string} staffId - Staff ID
   * @param {string} email - Staff member email
   * @returns {Promise<object>} - Result of the invitation process
   */
  inviteStaffMember: async (brandId, staffId, email) => {
    try {

      
      // Get staff data to use for user creation
      const staffData = await staffService.getStaffById(brandId, staffId);
      if (!staffData) {
        throw new Error('Staff data not found');
      }
      
      // Generate a random temporary password (will be reset immediately)
      const tempPassword = Math.random().toString(36).slice(-10) + Math.random().toString(36).toUpperCase().slice(-2);
      
      // Create a Firebase Authentication account for the staff member
      const userCredential = await createUserWithEmailAndPassword(auth, email, tempPassword);
      const user = userCredential.user;
      

      
      // Update the staff document with the Firebase UID
      const staffRef = doc(db, 'brands', brandId, 'staff', staffId);
      await updateDoc(staffRef, {
        uid: user.uid,
        inviteSent: true,
        inviteSentAt: new Date().toISOString()
      });
      

      
      // Create a user document in Firestore for the staff member
      await userService.createUser(user.uid, {
        email: email,
        displayName: staffData.name || email.split('@')[0],
        userType: 'staff',
        brandId: brandId,  // Associate staff with their brand
        staffId: staffId,  // Reference to staff document
        permissions: staffData.permissions || {},
        designation: staffData.designation || '',
        status: staffData.status || 'active',
        photoURL: '',
        createdAt: new Date().toISOString()
      });
      

      
      // Send custom password reset email to allow the staff member to set their own password
      const emailResult = await emailCustomizationService.sendCustomPasswordResetEmail(email);
      

      
      return {
        success: true,
        message: 'Invitation sent successfully',
        staffId,
        uid: user.uid
      };
    } catch (error) {
      console.error('Error inviting staff member:', error);
      
      // Handle common Firebase Auth errors
      let errorMessage = 'Failed to send invitation';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. The staff member can use their existing account.';
        
        // Update the staff document to mark invitation as sent anyway
        try {
          const staffRef = doc(db, 'brands', brandId, 'staff', staffId);
          await updateDoc(staffRef, {
            inviteSent: true,
            inviteSentAt: new Date().toISOString(),
            inviteError: 'Email already in use'
          });
        } catch (updateError) {
          console.error('Error updating staff document:', updateError);
        }
      }
      
      return {
        success: false,
        message: errorMessage,
        error: error.message
      };
    }
  },
  
  /**
   * Resend invitation email to a staff member
   * @param {string} email - Staff member email
   * @returns {Promise<object>} - Result of the resend process
   */
  resendInvitation: async (email) => {
    try {
      // Send password reset email via the Resend Cloud Function
      const result = await emailCustomizationService.sendCustomPasswordResetEmail(email);

      if (!result.success) {
        throw new Error(result.error || result.message);
      }

      return {
        success: true,
        message: 'Invitation resent successfully'
      };
    } catch (error) {
      console.error('Error resending invitation:', error);
      return {
        success: false,
        message: 'Failed to resend invitation',
        error: error.message
      };
    }
  }
};

export default staffInvitationService;
